import { useEffect, useMemo } from 'react';

import { errorApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useCloudAccountsByProductArea } from '../../utils/catalog';

const useStyles = makeStyles(theme => ({
  accountsContainer: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(1),
    maxHeight: '300px',
    overflowY: 'auto',
    border: `1px solid ${
      theme.palette.type === 'light'
        ? 'rgba(0, 0, 0, 0.23)'
        : 'rgba(255, 255, 255, 0.23)'
    }`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: 'transparent',
    transition: theme.transitions.create(['border-color'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      borderColor:
        theme.palette.type === 'light'
          ? 'rgba(0, 0, 0, 0.87)'
          : 'rgba(255, 255, 255, 0.87)',
    },
  },
  accountsContainerError: {
    borderColor: theme.palette.error.main,
    '&:hover': {
      borderColor: theme.palette.error.main,
    },
  },
  accountItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.25),
    padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    transition: theme.transitions.create(['background-color'], {
      duration: theme.transitions.duration.shortest,
    }),
    '&:last-child': {
      marginBottom: 0,
    },
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'light'
          ? 'rgba(0, 0, 0, 0.04)'
          : 'rgba(255, 255, 255, 0.08)',
    },
  },
  accountTitle: {
    fontWeight: 500,
    fontSize: '0.8125rem',
    flex: 1,
    color: theme.palette.text.primary,
  },
  chipContainer: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  chip: {
    height: '20px',
    fontSize: '0.7rem',
  },
}));

type CloudAccountData = {
  title: string;
  provider: string;
  accountId: string;
};

/**
 * CloudAccountPickerField - Displays cloud accounts associated with a Product Area
 *
 * This field gracefully handles all scenarios including:
 * - No Product Area selected: Shows prompt, initializes with empty array
 * - Loading state: Shows loading indicator
 * - Error fetching: Shows error message, passes empty array to form
 * - No cloud accounts: Shows friendly message, passes empty array (NOT an error)
 * - Has cloud accounts: Displays list, passes array of account objects
 *
 * Note: It's normal and expected for some Product Areas to have no cloud accounts.
 */
export const CloudAccountPickerField = (
  props: FieldExtensionComponentProps<CloudAccountData[]>,
) => {
  const {
    onChange,
    rawErrors,
    formContext,
    schema: {
      title = 'Cloud Accounts',
      description = 'Cloud accounts available for the selected Product Area',
    } = {},
  } = props;

  const catalogApi = useApi(catalogApiRef);
  const errorApi = useApi(errorApiRef);
  const classes = useStyles();

  // Extract product_area from form context
  const productAreaRef = formContext?.formData?.product_area as string;

  // Fetch cloud accounts filtered by the selected Product Area
  const { cloudAccounts, loading, error } = useCloudAccountsByProductArea(
    catalogApi,
    productAreaRef,
  );

  // Transform cloud account entities into a simplified data structure and sort alphabetically by title
  const cloudAccountData: CloudAccountData[] = useMemo(
    () =>
      cloudAccounts
        .map(account => ({
          title: account.metadata.title || account.metadata.name,
          provider: account.metadata.annotations?.['cloud.provider'] || 'unknown',
          accountId: account.metadata.annotations?.['cloud.id'] || '',
        }))
        .filter(account => account.accountId) // Only include accounts with valid IDs
        .sort((a, b) => a.title.localeCompare(b.title)),
    [cloudAccounts],
  );

  // Automatically update form data with the cloud accounts list whenever it changes
  // Always call onChange to ensure the field is properly initialized, even with empty array
  useEffect(() => {
    if (!loading) {
      // Always pass the data, even if empty or if there's an error
      // This ensures the field is properly initialized and subsequent action steps
      // can handle the empty array case gracefully
      onChange(error ? [] : cloudAccountData);
    }
  }, [cloudAccountData, loading, error, onChange]);

  const allErrors = [...(rawErrors || [])];
  const errorText = allErrors?.[0] as string | undefined;

  // Initialize field with empty array when no Product Area is selected
  useEffect(() => {
    if (!productAreaRef) {
      onChange([]);
    }
  }, [productAreaRef, onChange]);

  // Show message when no Product Area is selected
  if (!productAreaRef) {
    return (
      <Box>
        <FormControl fullWidth margin="normal" error={Boolean(errorText)}>
          <TextField
            label={title}
            value=""
            disabled
            placeholder="Select a Product Area first..."
            helperText="Please select a Product Area above to see available cloud accounts"
            variant="outlined"
          />
        </FormControl>
      </Box>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <Box>
        <FormControl fullWidth margin="normal">
          <TextField
            label={title}
            value=""
            disabled
            placeholder="Loading cloud accounts..."
            helperText="Loading cloud accounts for the selected Product Area..."
            variant="outlined"
          />
        </FormControl>
      </Box>
    );
  }

  // Show error state
  if (error) {
    // Log error for debugging
    errorApi.post(
      new Error(
        `Error fetching cloud accounts for Product Area: ${error.message}`,
      ),
    );

    return (
      <Box>
        <FormControl fullWidth margin="normal" error>
          <TextField
            label={title}
            value=""
            disabled
            placeholder="Error loading cloud accounts"
            helperText="Failed to load cloud accounts. Please try again or contact support."
            variant="outlined"
            error
          />
        </FormControl>
      </Box>
    );
  }

  // Show empty state when no cloud accounts are available
  // This is not an error - some product areas may not have cloud accounts
  if (cloudAccounts.length === 0) {
    return (
      <Box>
        <FormControl fullWidth margin="normal">
          <TextField
            label={title}
            value=""
            disabled
            placeholder="No cloud accounts available"
            helperText="No cloud accounts are associated with the selected Product Area."
            variant="outlined"
            // Don't show as required when empty - it's informational only
          />
        </FormControl>
      </Box>
    );
  }

  // Display cloud accounts list (read-only)
  return (
    <Box>
      <FormControl fullWidth margin="normal" error={Boolean(errorText)}>
        <InputLabel shrink htmlFor="cloud-accounts-list">
          {title}
        </InputLabel>
        <Box
          id="cloud-accounts-list"
          role="region"
          aria-label={title}
          aria-describedby="cloud-accounts-helper-text"
          className={`${classes.accountsContainer} ${
            errorText ? classes.accountsContainerError : ''
          }`}
        >
          {cloudAccountData.map((account, index) => (
            <Box key={`${account.accountId}-${index}`} className={classes.accountItem}>
              <Typography variant="body2" className={classes.accountTitle}>
                {account.title}
              </Typography>
              <Box className={classes.chipContainer}>
                <Chip
                  label={account.provider.toUpperCase()}
                  size="small"
                  className={classes.chip}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={account.accountId}
                  size="small"
                  className={classes.chip}
                  variant="outlined"
                />
              </Box>
            </Box>
          ))}
        </Box>
        <FormHelperText id="cloud-accounts-helper-text">
          {errorText ||
            description ||
            `Found ${cloudAccountData.length} cloud account(s) for the selected Product Area. This list will be available to subsequent action steps.`}
        </FormHelperText>
      </FormControl>
    </Box>
  );
};
